/**
 * «А мне-то можно?» — эффективные права текущего аккаунта в активной компании.
 *
 * ## Почему это вообще появилось, если кабинет прав не спрашивает
 *
 * Общее правило кабинета обратное и остаётся в силе: раздел не прячут потому, что
 * политика МОЖЕТ отказать, — про отказ узнают из `403`, как про всё остальное
 * (`conventions.md` §6). Оно исходит из того, что худшее последствие лишней кнопки —
 * сообщение об ошибке.
 *
 * Для зарплаты это неверно, и потому здесь исключение. Ресурс `salaries` завели
 * отдельно от `personnel` ровно затем, чтобы оклад не видел всякий, кто видит людей;
 * колонка «Оклад», нарисованная у оператора на проходной и заполнившаяся прочерками
 * после `403`, всё равно сообщила бы ему, что у компании есть ведомость и где она.
 * Поэтому раздел ведомости и колонку оклада показывают ТОЛЬКО при `salaries/read`, а
 * кнопки «завести» и «удалить» — при `create` и `delete`.
 *
 * ## Форма ответа, о которую легко споткнуться
 *
 * `GET /permissions/me?domain=company:{id}` перечисляет **только разрешённое**. Поля
 * `allowed` там нет вовсе — отсутствие пары в списке И ЕСТЬ «права нет». Список
 * никогда не был схемой: `object` в политике — свободный текст, поэтому по отсутствию
 * ресурса нельзя судить, что такого ресурса не бывает.
 *
 * Маршрут waived от самой проверки Casbin и не читает `x-company-id`: единственная его
 * область видимости — параметр `domain`. Отсюда `fetchMyPermissions`, а не
 * `requestInCompany`.
 */
import {useQuery} from '@tanstack/react-query';

import {useAuthStore} from '@/entities/session/auth-store';
import {fetchMyPermissions} from './api';
import {companyDomain} from './domain';
import type {EffectivePermission} from './types';

/** Пять минут — как в админской консоли; смена роли доезжает сама. */
const PERMISSIONS_STALE_MS = 5 * 60 * 1000;

export type PermissionCheck = (resource: string, action: string) => boolean;

export type CompanyAccess = {
  /** Разрешена ли пара. До ответа сервера — всегда `false`: закрыто по умолчанию. */
  can: PermissionCheck;
  /** Ответ ещё не пришёл. Экран может показать скелет вместо «доступа нет». */
  isPending: boolean;
  /** Спросить не удалось. Не то же самое, что «нельзя», — см. {@link useSalaryAccess}. */
  isError: boolean;
  refetch: () => void;
};

/**
 * Эффективные права в домене `company:{id}`.
 *
 * Ответ **закрыт по умолчанию**: пока запрос летит и если он не долетел, `can`
 * отвечает `false`. Открывать доступ на время загрузки значило бы показать оклад на
 * полсекунды тому, кому его видеть нельзя, — а полсекунды достаточно.
 */
export function useCompanyPermissions(
  companyId: string | undefined,
): CompanyAccess {
  const query = useQuery({
    queryKey: ['permissions-me', companyId],
    queryFn: () => fetchMyPermissions(companyDomain(companyId as string)),
    enabled: !!companyId,
    staleTime: PERMISSIONS_STALE_MS,
  });

  const permissions: EffectivePermission[] = query.data ?? [];

  return {
    can: (resource, action) =>
      permissions.some(p => p.resource === resource && p.action === action),
    isPending: !!companyId && query.isPending,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

export type SalaryAccess = {
  /** Показывать ли раздел ведомости, историю ставок и колонку оклада. */
  canRead: boolean;
  /** Показывать ли «Добавить ставку». */
  canCreate: boolean;
  /** Показывать ли «Удалить» у записи истории. */
  canDelete: boolean;
  /** Права ещё выясняются — не рисуйте пока ни раздел, ни «доступа нет». */
  isPending: boolean;
};

/**
 * Что этому человеку можно с зарплатой.
 *
 * ## Про владельца
 *
 * Владелец компании получает доступ и без `/permissions/me`. Это не обход проверки:
 * `activeContext.role === 'owner'` выводится из `owner_account_id === accountId`,
 * то есть из того же факта, из которого политика выводит `owner  company:*  *  manage`,
 * а сервер всё равно проверяет каждый вызов сам. Смысл ветки в том, что она переживает
 * рассинхрон: пока `/permissions/me` не перечислит новый ресурс (а перечислять их он
 * начал не сразу — см. changelog 2026-08-24), владелец не должен остаться без
 * собственной ведомости, глядя на экран без единой кнопки и без единой ошибки.
 *
 * Всем остальным ролям — строго по списку разрешённого. У «руководителя отдела» и
 * «наблюдателя» там стоит явный запрет, и `403` для них — штатное состояние.
 */
export function useSalaryAccess(companyId: string | undefined): SalaryAccess {
  const {can, isPending} = useCompanyPermissions(companyId);
  const isOwner = useAuthStore(
    s =>
      s.activeContext?.kind === 'company' &&
      s.activeContext.companyId === companyId &&
      s.activeContext.role === 'owner',
  );

  return {
    canRead: isOwner || can('salaries', 'read'),
    canCreate: isOwner || can('salaries', 'create'),
    canDelete: isOwner || can('salaries', 'delete'),
    // Владельцу ждать нечего: его доступ решён до всякого запроса.
    isPending: !isOwner && isPending,
  };
}
